import { useMemo, useState } from "react";
import { Aviso, Cartao } from "../componentes/ui";
import { AtaPublicada } from "../componentes/reuniao";
import { mensagemDoConsole } from "../nucleo/console";
import { useImplantacaoDoCliente } from "../nucleo/implantacoes";
import {
  formatarQuando,
  useAtaDaReuniao,
  useReunioes,
  type Reuniao,
} from "../nucleo/reuniao";
import { useTitulo } from "../nucleo/titulo";

export function MateriaisDoCliente() {
  useTitulo("Materiais");
  const { pendente, erro, implantacaoId, acessoDeConsulta, semAcesso } = useImplantacaoDoCliente();
  const reunioes = useReunioes(implantacaoId);
  const [busca, setBusca] = useState("");
  const treinamentos = useMemo(() => {
    const termo = busca.trim().toLocaleLowerCase("pt-BR");
    return (reunioes.data?.reunioes ?? [])
      .filter(
        (reuniao) =>
          reuniao.visivel_ao_cliente &&
          reuniao.tipo === "treinamento" &&
          reuniao.estado === "realizada",
      )
      .filter((reuniao) => !termo || reuniao.assunto.toLocaleLowerCase("pt-BR").includes(termo))
      .reverse();
  }, [busca, reunioes.data]);

  return (
    <>
      <h1 className="titulo-pagina">Central de materiais</h1>
      <p className="lead">
        As gravações e atas dos treinamentos da sua empresa, da mais recente para a mais antiga.
      </p>

      {/* A consulta das reuniões só é habilitada com uma implantação; desabilitada, ela fica
          "pendente" para sempre. Esperar por ela sem implantação prendia a tela no carregando —
          e escondia tanto a falha da esteira quanto o aviso de que ainda não há implantação. */}
      {pendente ? (
        <p className="lead" role="status">Carregando os materiais…</p>
      ) : semAcesso ? (
        <Aviso tom="marca" data-testid="materiais-fora-do-acesso">
          <b>O seu acesso é de consulta.</b> Ele não inclui os materiais da implantação. Se você
          precisa deles, peça a quem administra o acesso da sua empresa.
        </Aviso>
      ) : implantacaoId !== "" && reunioes.isPending ? (
        <p className="lead" role="status">Carregando os materiais…</p>
      ) : erro || reunioes.isError ? (
        <Aviso tom="erro">{mensagemDoConsole(erro ?? reunioes.error)}</Aviso>
      ) : implantacaoId === "" ? (
        <Aviso tom="marca">Os materiais aparecem quando a implantação for aberta.</Aviso>
      ) : (
        <>
          {acessoDeConsulta ? (
            <Aviso tom="marca" className="mb-4" data-testid="materiais-acesso-de-consulta">
              <b>O seu acesso é de consulta.</b> Você vê as gravações e as atas dos treinamentos,
              mas não o andamento das etapas.
            </Aviso>
          ) : null}
          <label className="campo mb-4">
            <span className="campo__rotulo">Buscar por tema</span>
            <input
              className="campo__controle"
              type="search"
              value={busca}
              onChange={(evento) => setBusca(evento.target.value)}
              placeholder="Ex.: treinamento de pedidos"
            />
          </label>
          {treinamentos.length ? (
            <div className="lista-de-itens" data-testid="gravacoes-dos-treinamentos">
              {treinamentos.map((reuniao) => (
                <MaterialDaReuniao key={reuniao.id} reuniao={reuniao} />
              ))}
            </div>
          ) : (
            <Aviso tom="marca" data-testid="sem-materiais">
              {busca
                ? "Nenhum treinamento corresponde a esta busca."
                : "Ainda não há treinamento realizado para mostrar aqui."}
            </Aviso>
          )}
        </>
      )}
    </>
  );
}

function MaterialDaReuniao({ reuniao }: { reuniao: Reuniao }) {
  const ata = useAtaDaReuniao(reuniao.id, reuniao.ata_publicada);
  const quando = formatarQuando(reuniao.realizada_em ?? reuniao.agendada_para) ?? "—";

  return (
    <section data-testid={`material-${reuniao.id}`}>
      <Cartao titulo={reuniao.assunto}>
        <p className="cartao__texto">Treinamento realizado em {quando}.</p>
        {reuniao.gravacoes.length ? (
          <ul className="lista-de-marcas">
            {reuniao.gravacoes.map((gravacao) => (
              <li className="lista-de-marcas__item" key={gravacao.id}>
                <a href={gravacao.arquivo_url}>{gravacao.nome}</a>
              </li>
            ))}
          </ul>
        ) : (
          <p className="nota-rodape">A gravação ainda não foi disponibilizada.</p>
        )}
      </Cartao>
      {ata.data?.publicada ? (
        <AtaPublicada ata={ata.data} titulo={`Ata · ${reuniao.assunto}`} />
      ) : null}
    </section>
  );
}
